(() => {
  const STOP_WORDS = new Set(['a', 'an', 'and', 'bike', 'bicycle', 'for', 'my', 'model', 'the', 'year']);

  const normalize = (value) => String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/ø/g, 'o')
    .replace(/[^a-z0-9.]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

  const asArray = (value) => Array.isArray(value) ? value.filter(Boolean) : [];
  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const uniqueBy = (items, key) => {
    const seen = new Set();
    return items.filter((item) => {
      const value = key(item);
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    });
  };

  const editDistance = (left, right) => {
    const a = normalize(left);
    const b = normalize(right);
    const row = Array.from({ length: b.length + 1 }, (_, index) => index);
    for (let i = 1; i <= a.length; i += 1) {
      let previous = row[0];
      row[0] = i;
      for (let j = 1; j <= b.length; j += 1) {
        const current = row[j];
        row[j] = Math.min(
          row[j] + 1,
          row[j - 1] + 1,
          previous + (a[i - 1] === b[j - 1] ? 0 : 1)
        );
        previous = current;
      }
    }
    return row[b.length];
  };

  class SeatpostMountPicker {
    constructor(root) {
      this.root = root;
      this.input = root.querySelector('[data-picker-input]');
      this.panel = root.querySelector('.seatpost-picker__panel');
      this.form = root.querySelector('[data-picker-search]');
      this.suggestions = root.querySelector('[data-picker-suggestions]');
      this.guidedToggle = root.querySelector('[data-guided-toggle]');
      this.guided = root.querySelector('[data-picker-guided]');
      this.brandSelect = root.querySelector('[data-picker-brand]');
      this.modelSelect = root.querySelector('[data-picker-model]');
      this.yearSelect = root.querySelector('[data-picker-year]');
      this.yearField = root.querySelector('[data-picker-year-field]');
      this.result = root.querySelector('[data-picker-result]');
      this.activeSuggestion = -1;

      try {
        this.records = JSON.parse(root.querySelector('[data-picker-records]').textContent || '[]');
      } catch (error) {
        this.records = [];
      }

      this.records = this.records.map((record) => this.prepareRecord(record));
      this.bindEvents();
      if (this.brandSelect) this.populateBrands();

      if (!this.records.length) {
        this.result.innerHTML = '<p class="seatpost-picker__empty-data">Compatibility data is being prepared. You can still browse every mount below.</p>';
      }
    }

    prepareRecord(record) {
      const aliases = asArray(record.aliases);
      const seatpostCodes = asArray(record.seatpostCodes);
      const excludedTerms = asArray(record.excludedTerms).map(normalize);
      const yearFrom = record.yearFrom === null || record.yearFrom === '' ? null : Number(record.yearFrom);
      const yearTo = record.yearTo === null || record.yearTo === '' ? null : Number(record.yearTo);
      const searchableParts = [record.brand, record.model, record.qualifier, record.diameterMm, ...aliases, ...seatpostCodes];
      const searchText = normalize(searchableParts.join(' '));

      return {
        ...record,
        aliases,
        seatpostCodes,
        excludedTerms,
        yearFrom,
        yearTo,
        searchText,
        tokens: searchText.split(' ').filter(Boolean),
        groupKey: `${normalize(record.brand)}::${normalize(record.model)}`
      };
    }

    bindEvents() {
      this.input.addEventListener('input', () => this.onInput());
      this.input.addEventListener('keydown', (event) => this.onInputKeydown(event));
      this.input.addEventListener('blur', () => window.setTimeout(() => this.closeSuggestions(), 120));
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.commitSearch(this.input.value);
      });
      this.result.addEventListener('click', (event) => {
        if (event.target.closest('[data-picker-reset]')) this.resetToSearch();
      });

      if (this.guidedToggle) {
        this.guidedToggle.addEventListener('click', () => {
          const isOpen = this.guidedToggle.getAttribute('aria-expanded') === 'true';
          this.guidedToggle.setAttribute('aria-expanded', String(!isOpen));
          this.guided.hidden = isOpen;
          if (!isOpen) this.brandSelect.focus();
        });

        this.brandSelect.addEventListener('change', () => this.onBrandChange());
        this.modelSelect.addEventListener('change', () => this.onModelChange());
        this.yearSelect.addEventListener('change', () => this.resolveGuidedSelection());
      }
    }

    showResultScreen() {
      this.closeSuggestions();
      this.panel.classList.add('seatpost-picker__panel--showing-result');
    }

    resetToSearch() {
      this.panel.classList.remove('seatpost-picker__panel--showing-result');
      this.result.innerHTML = '';
      if (this.guidedToggle) {
        this.guidedToggle.setAttribute('aria-expanded', 'false');
        this.guided.hidden = true;
      }
      this.input.focus();
      this.input.select();
    }

    resetMarkup() {
      const label = this.root.dataset.changeBikeLabel || 'Change bike';
      return `<button class="seatpost-picker__reset button--inline-underline" type="button" data-picker-reset>${escapeHtml(label)}</button>`;
    }

    label(record) {
      if (record.fitmentKind === 'round_seatpost' && record.diameterMm) {
        return `Round seatpost Ø${record.diameterMm} mm`;
      }
      return [record.brand, record.model].filter(Boolean).join(' ');
    }

    yearLabel(record) {
      if (!record.yearFrom && !record.yearTo) return '';
      if (record.yearFrom && !record.yearTo) return `${record.yearFrom}–present`;
      if (record.yearFrom === record.yearTo) return String(record.yearFrom);
      return `${record.yearFrom || 'up to'}–${record.yearTo}`;
    }

    suggestionMeta(record) {
      return [this.yearLabel(record), record.qualifier, record.seatpostCodes.join(', ')].filter(Boolean).join(' · ');
    }

    onInput() {
      // A rendered match belongs to the exact query that produced it. Clear it
      // as soon as the customer edits that query so a stale fit is never shown.
      this.result.innerHTML = '';
      const query = normalize(this.input.value);
      if (query.length < 2) {
        this.closeSuggestions();
        return;
      }
      this.renderSuggestions(this.suggest(query));
    }

    onInputKeydown(event) {
      const buttons = [...this.suggestions.querySelectorAll('button')];
      if (!buttons.length || this.suggestions.hidden) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        this.activeSuggestion = (this.activeSuggestion + delta + buttons.length) % buttons.length;
        buttons.forEach((button, index) => button.setAttribute('aria-selected', String(index === this.activeSuggestion)));
        this.input.setAttribute('aria-activedescendant', buttons[this.activeSuggestion].id);
      }

      if (event.key === 'Enter' && this.activeSuggestion >= 0) {
        event.preventDefault();
        buttons[this.activeSuggestion].click();
      }

      if (event.key === 'Escape') this.closeSuggestions();
    }

    suggest(query) {
      const queryTokens = query.split(' ').filter((token) => token && !STOP_WORDS.has(token));
      const scored = this.records.map((record) => {
        let score = 0;
        if (record.searchText === query) score += 200;
        if (record.searchText.includes(query)) score += 100;
        queryTokens.forEach((token) => {
          if (record.tokens.includes(token)) score += 30;
          else if (record.tokens.some((candidate) => candidate.startsWith(token) && token.length >= 2)) score += 18;
          else if (token.length >= 4 && record.tokens.some((candidate) => candidate.length >= 4 && editDistance(token, candidate) <= 1)) score += 5;
        });
        return { record, score };
      }).filter(({ score }) => score > 0).sort((a, b) => b.score - a.score);

      return uniqueBy(scored.map(({ record }) => record), (record) => `${record.groupKey}::${record.qualifier || ''}`).slice(0, 7);
    }

    renderSuggestions(records) {
      this.activeSuggestion = -1;
      if (!records.length) {
        this.closeSuggestions();
        return;
      }

      this.suggestions.innerHTML = records.map((record, index) => `
        <li role="presentation">
          <button id="${this.input.id}-option-${index}" class="seatpost-picker__suggestion" type="button" role="option" aria-selected="false" data-suggestion-index="${index}">
            <span class="seatpost-picker__suggestion-title">${escapeHtml(this.label(record))}</span>
            ${this.suggestionMeta(record) ? `<span class="seatpost-picker__suggestion-meta">${escapeHtml(this.suggestionMeta(record))}</span>` : ''}
          </button>
        </li>
      `).join('');

      this.suggestions.querySelectorAll('button').forEach((button, index) => {
        button.addEventListener('click', () => this.selectSuggestion(records[index]));
        button.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            this.selectSuggestion(records[index]);
          }
        });
      });

      this.suggestions.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    closeSuggestions() {
      this.suggestions.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.activeSuggestion = -1;
    }

    selectSuggestion(record) {
      this.input.value = this.label(record);
      this.closeSuggestions();
      const candidates = this.records.filter((candidate) => candidate.groupKey === record.groupKey);
      this.resolveCandidates(candidates, null);
    }

    meaningfulTokens(query) {
      return normalize(query).split(' ').filter((token) => token && !STOP_WORDS.has(token) && !/^\d{4}$/.test(token));
    }

    exactMatches(query) {
      const normalizedQuery = normalize(query);
      const tokens = this.meaningfulTokens(query);
      if (!tokens.length) return [];

      return this.records.filter((record) => {
        if (record.excludedTerms.some((term) => normalizedQuery.split(' ').includes(term))) return false;
        return tokens.every((token) => record.tokens.some((candidate) => candidate === token || (token.length >= 3 && candidate.startsWith(token))));
      });
    }

    commitSearch(query) {
      this.closeSuggestions();
      const matches = this.exactMatches(query);
      const yearMatch = String(query).match(/\b(?:19|20)\d{2}\b/);
      const year = yearMatch ? Number(yearMatch[0]) : null;

      if (!matches.length) {
        this.renderNoMatch();
        return;
      }

      const groups = new Set(matches.map((record) => record.groupKey));
      if (groups.size > 1) {
        this.renderRefine(matches);
        return;
      }

      this.resolveCandidates(matches, year);
    }

    yearFits(record, year) {
      if (!year) return true;
      if (record.yearFrom && year < record.yearFrom) return false;
      if (record.yearTo && year > record.yearTo) return false;
      return true;
    }

    resolveCandidates(candidates, year) {
      const needsYear = candidates.some((record) => record.yearFrom || record.yearTo);
      if (needsYear && !year) {
        this.renderYearPrompt(candidates);
        return;
      }

      const valid = candidates.filter((record) => this.yearFits(record, year));
      if (!valid.length) {
        this.renderNoMatch();
        return;
      }
      this.renderMatches(valid);
    }

    relevantYears(records) {
      const currentYear = new Date().getFullYear();
      const minimum = Math.min(...records.map((record) => record.yearFrom || currentYear - 20));
      const maximum = Math.max(...records.map((record) => record.yearTo || currentYear + 1));
      const years = [];
      for (let year = maximum; year >= minimum; year -= 1) years.push(year);
      return years;
    }

    renderYearPrompt(candidates) {
      const years = this.relevantYears(candidates);
      const bikeLabel = this.label(candidates[0]);
      const target = this.root.dataset.requestTarget || '#seatpost-mount-request-form';
      const missingText = this.root.dataset.yearMissingText || 'Can’t find your model year? We may not have a mount for that generation yet.';
      const missingLinkLabel = this.root.dataset.yearMissingLinkLabel || 'Request the mount';
      this.showResultScreen();
      this.result.innerHTML = `
        <div class="seatpost-picker__year-prompt">
          <p class="seatpost-picker__screen-label"><strong>${escapeHtml(bikeLabel)}</strong> — select model year</p>
          <div class="seatpost-picker__year-controls">
            <label class="visually-hidden" for="SeatpostPickerResultYear">Model year</label>
            <select id="SeatpostPickerResultYear">
              <option value="">Select model year</option>
              ${years.map((year) => `<option value="${year}">${year}</option>`).join('')}
            </select>
            <button class="button button--solid" type="button" disabled>Confirm year</button>
          </div>
          <p class="seatpost-picker__year-missing">
            ${escapeHtml(missingText)}
            <br><a href="${escapeHtml(target)}">${escapeHtml(missingLinkLabel)}</a>
          </p>
          ${this.resetMarkup()}
        </div>`;

      const select = this.result.querySelector('select');
      const button = this.result.querySelector('.seatpost-picker__year-controls button');
      select.addEventListener('change', () => { button.disabled = !select.value; });
      button.addEventListener('click', () => this.resolveCandidates(candidates, Number(select.value)));
      select.focus();
    }

    renderRefine(matches) {
      const suggestions = uniqueBy(matches, (record) => record.groupKey).slice(0, 7);
      this.showResultScreen();
      this.result.innerHTML = `
        <h3 class="seatpost-picker__result-heading">Choose the exact model</h3>
        <div class="seatpost-picker__cards">
          ${suggestions.map((record, index) => `
            <button class="seatpost-picker__suggestion" type="button" data-refine-index="${index}">
              <span class="seatpost-picker__suggestion-title">${escapeHtml(this.label(record))}</span>
              ${this.suggestionMeta(record) ? `<span class="seatpost-picker__suggestion-meta">${escapeHtml(this.suggestionMeta(record))}</span>` : ''}
            </button>`).join('')}
        </div>
        ${this.resetMarkup()}`;
      this.result.querySelectorAll('[data-refine-index]').forEach((button, index) => {
        button.addEventListener('click', () => this.selectSuggestion(suggestions[index]));
      });
    }

    renderMatches(records) {
      const products = uniqueBy(records, (record) => `${record.product.url}::${record.qualifier || ''}`);
      const copy = products.length > 1
        ? 'More than one verified option exists for this bike. Choose where you want to mount the device.'
        : '';

      this.showResultScreen();
      this.result.innerHTML = `
        <div class="seatpost-picker__match-result">
          ${copy ? `<p class="seatpost-picker__result-copy">${copy}</p>` : ''}
          <div class="seatpost-picker__cards">
            ${products.map((record) => this.cardMarkup(record)).join('')}
          </div>
          ${this.resetMarkup()}
        </div>`;
    }

    cardMarkup(record) {
      const meta = [this.yearLabel(record), record.qualifier].filter(Boolean).join(' · ');
      return `
        <article class="seatpost-picker__card">
          ${record.product.image ? `<img class="seatpost-picker__card-image" src="${escapeHtml(record.product.image)}" alt="" loading="lazy" width="80" height="80">` : '<div class="seatpost-picker__card-image"></div>'}
          <div class="seatpost-picker__card-content">
            <h4 class="seatpost-picker__card-title">${escapeHtml(record.product.title)}</h4>
            ${meta ? `<p class="seatpost-picker__card-meta">${escapeHtml(meta)}</p>` : ''}
            ${record.note ? `<p class="seatpost-picker__card-note">${escapeHtml(record.note)}</p>` : ''}
          </div>
          <a class="seatpost-picker__card-link" href="${escapeHtml(record.product.url)}" aria-label="${escapeHtml(`${this.root.dataset.viewLabel}: ${record.product.title}`)}">
            <span class="visually-hidden">${escapeHtml(this.root.dataset.viewLabel)}</span>
          </a>
          <a class="button button--solid" href="${escapeHtml(record.product.url)}">${escapeHtml(this.root.dataset.viewLabel)}</a>
        </article>`;
    }

    renderNoMatch() {
      const target = this.root.dataset.requestTarget || '#seatpost-mount-request-form';
      this.showResultScreen();
      this.result.innerHTML = `
        <div class="seatpost-picker__no-match">
          <p class="seatpost-picker__result-copy">${escapeHtml(this.root.dataset.noMatchText)}</p>
          <a class="button button--solid" href="${escapeHtml(target)}">${escapeHtml(this.root.dataset.requestLabel)}</a>
          ${this.resetMarkup()}
        </div>`;
    }

    populateBrands() {
      const brands = [...new Set(this.records.map((record) => record.brand).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));
      this.brandSelect.insertAdjacentHTML('beforeend', brands.map((brand) => `<option value="${escapeHtml(brand)}">${escapeHtml(brand)}</option>`).join(''));
    }

    onBrandChange() {
      const brand = normalize(this.brandSelect.value);
      const records = this.records.filter((record) => normalize(record.brand) === brand);
      const models = uniqueBy(records, (record) => normalize(record.model)).sort((a, b) => this.label(a).localeCompare(this.label(b)));

      this.modelSelect.innerHTML = `<option value="">${escapeHtml(this.modelSelect.options[0]?.text || 'Choose model')}</option>`;
      this.modelSelect.insertAdjacentHTML('beforeend', models.map((record) => `<option value="${escapeHtml(record.model)}">${escapeHtml(record.model)}</option>`).join(''));
      this.modelSelect.disabled = !brand;
      this.yearField.hidden = true;
      this.yearSelect.innerHTML = '<option value="">Choose year</option>';
      this.result.innerHTML = '';
    }

    onModelChange() {
      const candidates = this.guidedCandidates();
      if (!candidates.length) return;
      const needsYear = candidates.some((record) => record.yearFrom || record.yearTo);
      this.yearField.hidden = !needsYear;

      if (needsYear) {
        this.yearSelect.innerHTML = '<option value="">Choose year</option>' + this.relevantYears(candidates)
          .map((year) => `<option value="${year}">${year}</option>`).join('');
        this.result.innerHTML = '';
      } else {
        this.resolveCandidates(candidates, null);
      }
    }

    guidedCandidates() {
      const brand = normalize(this.brandSelect.value);
      const model = normalize(this.modelSelect.value);
      return this.records.filter((record) => normalize(record.brand) === brand && normalize(record.model) === model);
    }

    resolveGuidedSelection() {
      const candidates = this.guidedCandidates();
      if (!candidates.length || !this.yearSelect.value) return;
      this.resolveCandidates(candidates, Number(this.yearSelect.value));
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-seatpost-picker]').forEach((root) => {
      if (!root.dataset.pickerInitialized) {
        root.dataset.pickerInitialized = 'true';
        new SeatpostMountPicker(root);
      }
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
